import { getReferenceString, MedplumClient } from '@medplum/core';
import { Appointment, EpisodeOfCare, Patient } from '@medplum/fhirtypes';
import { APPOINTMENT_TYPES_SYSTEM, PROCEDURE_APPOINTMENT_CODES } from '@bariendo/lib/constants';
import { getPrimaryEmail, getPrimaryPhone } from '@/services/contact-point';
import {
  formatProcedureType,
  getBariatricProcedureStageLabel,
  getEmployer,
  getTrackLabel,
  isEmployerCoverageTrackLabel,
} from '@/services/patient-fields';
import { formatDate } from '@/services/date';
import { rowsToCsv } from '@/services/csv';

/** One flat, fully-stringified export row. Empty string means "absent" — never undefined. */
export interface PatientExportRow {
  patientId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  birthDate: string;
  procedureDate: string;
  procedureType: string;
  site: string;
  track: string;
  employer: string;
  currentState: string;
}

/**
 * CSV headers in display order. Reuses the Carrum report's vocabulary (`Procedure`, `Employer`,
 * `Current state`) so Orbit's two exports read alike — see CARRUM_CSV_HEADERS.
 */
export const PATIENT_EXPORT_CSV_HEADERS = [
  'First name',
  'Last name',
  'Email',
  'Mobile',
  'Date of birth',
  'Procedure',
  'Procedure type',
  'Site',
  'Track',
  'Employer',
  'Current state',
  'Patient ID',
];

/** How many patient ids go into one search request. */
export const EXPORT_ID_CHUNK_SIZE = 100;

/**
 * Shown in the Track column when a patient has no track episode. Rendered as a real value rather
 * than left blank so the column is filterable and honest: only Carrum patients carry episodes today,
 * so absence means "not recorded", not "self-pay". Labelling it Self-pay would silently assert a
 * payer for a patient who might be Transcarent.
 */
export const UNKNOWN_TRACK_LABEL = 'Unknown';

const LOCATION_REFERENCE_PREFIX = 'Location/';
const PATIENT_REFERENCE_PREFIX = 'Patient/';

/** Reads the procedure site's display name from the appointment's Location participant. */
function getSite(appointment?: Appointment): string {
  const locationParticipant = appointment?.participant?.find((participant) =>
    participant.actor?.reference?.startsWith(LOCATION_REFERENCE_PREFIX)
  );

  return locationParticipant?.actor?.display ?? '';
}

export function buildExportRow(input: {
  patient: Patient;
  episodes: EpisodeOfCare[];
  procedureAppointment?: Appointment;
}): PatientExportRow {
  const { patient, episodes, procedureAppointment } = input;
  const name = patient.name?.[0];

  return {
    patientId: patient.id ?? '',
    firstName: name?.given?.[0] ?? '',
    lastName: name?.family ?? '',
    email: getPrimaryEmail(patient.telecom)?.value ?? '',
    mobile: getPrimaryPhone(patient.telecom)?.value ?? '',
    birthDate: formatDate(patient.birthDate),
    procedureDate: formatDate(procedureAppointment?.start),
    procedureType: formatProcedureType(procedureAppointment),
    site: getSite(procedureAppointment),
    track: getTrackLabel(episodes) || UNKNOWN_TRACK_LABEL,
    employer: getEmployer(patient),
    currentState: getBariatricProcedureStageLabel(patient),
  };
}

/**
 * True when the row's patient is on an employer-sponsored track. Drives the "Exclude employer
 * coverage" control — rows with an unknown track are kept, since absence of an episode is not
 * evidence of employer coverage.
 */
export function isEmployerCoverageRow(row: PatientExportRow): boolean {
  return isEmployerCoverageTrackLabel(row.track);
}

/** Serializes a row into CSV cells matching PATIENT_EXPORT_CSV_HEADERS order. */
export function exportRowToCsvCells(row: PatientExportRow): string[] {
  return [
    row.firstName,
    row.lastName,
    row.email,
    row.mobile,
    row.birthDate,
    row.procedureDate,
    row.procedureType,
    row.site,
    row.track,
    row.employer,
    row.currentState,
    row.patientId,
  ];
}

export function exportRowsToCsv(rows: PatientExportRow[]): string {
  return rowsToCsv(PATIENT_EXPORT_CSV_HEADERS, rows.map(exportRowToCsvCells));
}

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

function groupEpisodesByPatient(episodes: EpisodeOfCare[]): Map<string, EpisodeOfCare[]> {
  const byPatient = new Map<string, EpisodeOfCare[]>();
  for (const episode of episodes) {
    const reference = episode.patient?.reference;
    if (!reference) {
      continue;
    }
    const existing = byPatient.get(reference);
    if (existing) {
      existing.push(episode);
    } else {
      byPatient.set(reference, [episode]);
    }
  }
  return byPatient;
}

/** Maps each patient reference to their most recent procedure appointment (search is `-date` sorted). */
function mapLatestAppointmentByPatient(appointments: Appointment[]): Map<string, Appointment> {
  const byPatient = new Map<string, Appointment>();
  for (const appointment of appointments) {
    for (const participant of appointment.participant ?? []) {
      const reference = participant.actor?.reference;
      if (reference?.startsWith(PATIENT_REFERENCE_PREFIX) && !byPatient.has(reference)) {
        byPatient.set(reference, appointment);
      }
    }
  }
  return byPatient;
}

/**
 * Reads every patient in the cohort plus their episodes and latest procedure appointment, and
 * flattens them into export rows. Chunks the id list so search params stay a sane length.
 *
 * REST rather than GraphQL on purpose: it avoids the `_has` + union-field bug that silently returns
 * zero rows, and it exercises correctly under MockClient.
 */
export async function fetchExportRows(
  medplum: MedplumClient,
  patientIds: string[],
  chunkSize: number = EXPORT_ID_CHUNK_SIZE
): Promise<PatientExportRow[]> {
  if (patientIds.length === 0) {
    return [];
  }

  const rows: PatientExportRow[] = [];

  for (const idChunk of chunk(patientIds, chunkSize)) {
    const patientReferences = idChunk.map((id) => getReferenceString({ resourceType: 'Patient', id })).join(',');

    const [patients, episodes, appointments] = await Promise.all([
      medplum.searchResources('Patient', { _id: idChunk.join(','), _count: chunkSize }),
      medplum.searchResources('EpisodeOfCare', { patient: patientReferences, _count: chunkSize * 4 }),
      medplum.searchResources('Appointment', {
        patient: patientReferences,
        'appointment-type': PROCEDURE_APPOINTMENT_CODES.map((code) => `${APPOINTMENT_TYPES_SYSTEM}|${code}`).join(','),
        _sort: '-date',
        _count: chunkSize * 4,
      }),
    ]);

    const episodesByPatient = groupEpisodesByPatient(episodes);
    const appointmentByPatient = mapLatestAppointmentByPatient(appointments);

    for (const patient of patients) {
      const reference = getReferenceString(patient);
      rows.push(
        buildExportRow({
          patient,
          episodes: episodesByPatient.get(reference) ?? [],
          procedureAppointment: appointmentByPatient.get(reference),
        })
      );
    }
  }

  return rows;
}
